import { comfortableRangeFraction } from './constants.js'
import type { VocalRange } from './types.js'

/**
 * Shift a phrase by whole octaves so its centre lands in the middle 60 % of the singer's
 * calibrated range (§5.8).
 *
 * Whole octaves only: transposing by anything else would change the exercise. A phrase's centre
 * is the midpoint of its own lowest and highest note, which is what "where the phrase sits"
 * means to a singer looking at it.
 */
export const transposeIntoRange = (reference: number[], range: VocalRange): number[] => {
  if (reference.length === 0) {
    return reference
  }

  const margin = ((range.high - range.low) * (1 - comfortableRangeFraction)) / 2
  const bandLow = range.low + margin
  const bandHigh = range.high - margin
  const bandCentre = (bandLow + bandHigh) / 2

  const centre = (Math.min(...reference) + Math.max(...reference)) / 2

  /** How far outside the comfortable band the phrase's centre would sit after `octaves`. */
  const outside = (octaves: number) => {
    const shifted = centre + octaves * 12
    if (shifted < bandLow) {
      return bandLow - shifted
    }
    return shifted > bandHigh ? shifted - bandHigh : 0
  }

  /** The real-valued shift landing the centre on the band centre, rounded both ways. */
  const ideal = (bandCentre - centre) / 12
  const candidates = [Math.floor(ideal), Math.ceil(ideal)]

  const best = candidates.reduce((chosen, candidate) => {
    const chosenOutside = outside(chosen)
    const candidateOutside = outside(candidate)
    if (candidateOutside !== chosenOutside) {
      return candidateOutside < chosenOutside ? candidate : chosen
    }
    /** Equally far outside — prefer the one sitting nearer the middle of the band. */
    const chosenDistance = Math.abs(centre + chosen * 12 - bandCentre)
    const candidateDistance = Math.abs(centre + candidate * 12 - bandCentre)
    return candidateDistance < chosenDistance ? candidate : chosen
  })

  return best === 0 ? [...reference] : reference.map(midi => midi + best * 12)
}
